import {copyTextToClipboard} from '@helpers/clipboard';
import {attachClickEvent} from '@helpers/dom/clickEvent';
import ListenerSetter from '@helpers/listenerSetter';
import wrapPlainText from '@lib/richTextProcessor/wrapPlainText';
import Button from '@components/button';
import ButtonIcon from '@components/buttonIcon';
import ButtonMenuToggle from '@components/buttonMenuToggle';
import {MiddleEllipsisElement} from '@components/middleEllipsis';
import shareUrlToPeers from '@components/popups/shareUrl';
import ripple from '@components/ripple';
import {toastNew} from '@components/toast';

export class InviteLink {
  public container: HTMLDivElement;
  public textElement: HTMLDivElement;
  public button: HTMLButtonElement;
  public buttonText: HTMLSpanElement;
  public onButtonClick: () => void;

  public url: string;

  constructor({
    buttons,
    button,
    onButtonClick,
    listenerSetter,
    url,
    noRightButton,
    onClick,
    class: className
  }: {
    buttons?: Parameters<typeof ButtonMenuToggle>[0]['buttons'],
    /**
     * The action under the link. An array puts them side by side in one row —
     * the Call Link box needs Share and Copy together.
     */
    button?: HTMLButtonElement | HTMLButtonElement[] | false,
    onButtonClick?: () => void,
    listenerSetter: ListenerSetter,
    url?: string,
    noRightButton?: boolean,
    onClick?: () => void,
    /** Extra class on the container, for a caller that places it itself. */
    class?: string
  }) {
    this.onButtonClick = onButtonClick;

    const linkContainer = this.container = document.createElement('div');
    linkContainer.classList.add('invite-link-container');
    if(className) linkContainer.classList.add(...className.split(' ').filter(Boolean));

    const link = document.createElement('div');
    link.classList.add('invite-link', 'rp-overflow');

    const text = this.textElement = document.createElement('div');
    text.classList.add('invite-link-text');

    let rightButton: HTMLElement;
    if(buttons) {
      rightButton = ButtonMenuToggle({
        buttons,
        direction: 'bottom-left',
        buttonOptions: {noRipple: true},
        listenerSetter
      });
    } else if(!noRightButton) {
      rightButton = ButtonIcon('copy', {noRipple: true});
      attachClickEvent(rightButton, () => this.copyLink(), {listenerSetter});
    }

    if(rightButton) rightButton.classList.add('invite-link-menu');

    if(!button && button !== false) {
      button = Button('', {text: 'ShareLink'});
      this.buttonText = button.lastElementChild as HTMLSpanElement;
      attachClickEvent(button, () => {
        if(this.onButtonClick) this.onButtonClick();
        else this.shareLink();
      }, {listenerSetter});
    }

    const buttonElements = button ? (Array.isArray(button) ? button : [button]) : [];
    buttonElements.forEach((element) => {
      element.className = 'btn-primary btn-color-primary invite-link-button';
    });
    this.button = buttonElements[0];

    let buttonsElement: HTMLElement;
    if(buttonElements.length > 1) {
      buttonsElement = document.createElement('div');
      buttonsElement.classList.add('invite-link-buttons');
      buttonsElement.append(...buttonElements);
    } else {
      buttonsElement = buttonElements[0];
    }

    if(url) this.setUrl(url);
    ripple(link);
    link.append(...[
      text,
      rightButton
    ].filter(Boolean));

    linkContainer.append(link, buttonsElement || '');

    attachClickEvent(link, onClick || (() => this.copyLink()), {listenerSetter});
  }

  public setUrl(url: string) {
    let s = url;
    if(s.includes('//')) {
      s = url.split('//').slice(1).join('//');
    }

    // Middle truncation, so both ends of the link survive a narrow box: the
    // same element documents and audio trim their file names with.
    const element = new MiddleEllipsisElement();
    element.textContent = wrapPlainText(s);

    this.textElement.replaceChildren(element);
    this.url = url;
  }

  public copyLink = (url: string = this.url) => {
    copyTextToClipboard(url);
    toastNew({langPackKey: 'LinkCopied'});
  };

  public shareLink = (url: string = this.url) => {
    shareUrlToPeers({url, openAfter: true});
  };
}
